import { LINE_TYPES } from "@/data/constants"
import { cssVar } from "nvd-js-helpers/misc"
import { canvasHelper } from "@/modules/canvas/canvas-helper.js"
import {
    connectionDirection,
    distanceToSegment,
    nearestSegmentIndex,
    normalizeConnectionRoute,
    sampleBezier,
} from "@/modules/canvas/connection-routing.js"

export class Connection {
    canvas
    fromRow
    toRow
    laneIndex = 0
    laneCount = 1
    pathPoints = []
    manualWaypoints = []
    fromSide = 'right'
    toSide = 'left'

    constructor(canvas, row) {
        this.canvas = canvas
        this.fromRow = row
    }

    get route() {
        return normalizeConnectionRoute(this.fromRow.data?.connection_route)
    }

    get selected() {
        return !this.canvas.exporting && this.canvas.isConnectionSelected?.(this)
    }

    get hovered() {
        return !this.canvas.exporting && this.canvas.isConnectionHovered?.(this)
    }

    direction() {
        return connectionDirection(this.fromRow, this.toRow, this.route)
    }

    setLane(index, count) {
        this.laneIndex = index
        this.laneCount = count
    }

    draw() {
        this.drawConnection(...this.direction())
    }

    endpointY(row, target = false) {
        if (!target || this.laneCount <= 1) {
            return Math.round(row.top + row.height() / 2)
        }

        const padding = Math.min(3, row.height() * 0.15)
        const availableHeight = row.height() - padding * 2
        return Math.round(row.top + padding + availableHeight * ((this.laneIndex + 1) / (this.laneCount + 1)))
    }

    automaticPath(lineType, start, end, controlOne, controlTwo) {
        if (lineType === LINE_TYPES.FLOWCHART) {
            let channelX
            if (this.fromSide === this.toSide) {
                const sideDirection = this.fromSide === 'right' ? 1 : -1
                const outerEdge = sideDirection > 0
                    ? Math.max(start.x, end.x)
                    : Math.min(start.x, end.x)
                channelX = outerEdge + sideDirection * (30 + this.laneIndex * 16)
            } else {
                channelX = (start.x + end.x) / 2
                const direction = end.x >= start.x ? 1 : -1
                channelX += direction * this.laneIndex * 12
            }
            return [
                start,
                { x: channelX, y: start.y },
                { x: channelX, y: end.y },
                end,
            ]
        }

        if (lineType === LINE_TYPES.STRAIGHT) {
            return [start, controlOne, controlTwo, end]
        }

        return sampleBezier(start, controlOne, controlTwo, end)
    }

    drawPath(ctx, lineType, start, end, controlOne, controlTwo) {
        const route = this.route
        this.manualWaypoints = route?.waypoints || []

        if (this.manualWaypoints.length) {
            this.pathPoints = [start, ...this.manualWaypoints, end]
            this.pathPoints.slice(1).forEach(point => ctx.lineTo(point.x, point.y))
            return
        }

        this.pathPoints = this.automaticPath(lineType, start, end, controlOne, controlTwo)
        if (lineType === LINE_TYPES.BEZIER) {
            ctx.bezierCurveTo(controlOne.x, controlOne.y, controlTwo.x, controlTwo.y, end.x, end.y)
            return
        }

        this.pathPoints.slice(1).forEach(point => ctx.lineTo(point.x, point.y))
    }

    drawHandles(ctx) {
        if (!this.selected || this.canvas.exporting) return

        const handles = this.manualWaypoints.length
            ? this.manualWaypoints
            : [this.midpoint()]

        ctx.save()
        ctx.globalCompositeOperation = 'source-over'
        for (const point of handles) {
            ctx.beginPath()
            ctx.arc(point.x, point.y, 5 / this.canvas.zoom.scale, 0, Math.PI * 2)
            ctx.fillStyle = cssVar('--body-bg')
            ctx.fill()
            ctx.lineWidth = 2 / this.canvas.zoom.scale
            ctx.strokeStyle = cssVar('--primary')
            ctx.stroke()
            ctx.closePath()
        }
        ctx.restore()
    }

    drawConnection(from, to) {
        this.fromSide = from
        this.toSide = to

        const start = {
            x: this.fromRow[from],
            y: this.endpointY(this.fromRow),
        }
        const end = {
            x: this.toRow[to],
            y: this.endpointY(this.toRow, true),
        }
        const ctx = this.canvas.ctx

        ctx.save()
        try {
            const directions = { left: -1, right: 1 }
            const lineType = this.canvas?.settings?.lineType || LINE_TYPES.BEZIER
            const baseOffset = lineType === LINE_TYPES.FLOWCHART ? 30 : (lineType === LINE_TYPES.STRAIGHT ? 20 : 100)
            const targetLaneOffset = this.laneIndex * 16
            const endPointSize = 3
            const arrowHeight = 5
            const arrowLength = 8
            let color = this.toRow?.table?.color || this.canvas?.settings?.lineColor || '#bbb'
            let lineWidth = 1

            const isFromHovered = this.fromRow.table.isHoveredObject
            const isToHovered = this.toRow.table.isHoveredObject
            if (isFromHovered || isToHovered) {
                color = this.toRow?.table?.color || cssVar('--primary')
                lineWidth = 2

                this.toRow.highlighted = true
                if (isToHovered) this.fromRow.highlighted = true

                ctx.globalCompositeOperation = 'source-over'
                this.toRow.draw()
                if (isToHovered) this.fromRow.draw()
                ctx.globalCompositeOperation = 'destination-over'
            }

            if (this.hovered || this.selected) {
                color = cssVar('--primary')
                lineWidth = this.selected ? 2.5 : 2
            }

            ctx.lineWidth = lineWidth
            ctx.fillStyle = color
            ctx.strokeStyle = color

            const controlOne = {
                x: start.x + baseOffset * directions[from],
                y: start.y,
            }
            const controlTwo = {
                x: end.x + (baseOffset + targetLaneOffset) * directions[to],
                y: end.y,
            }

            ctx.beginPath()
            if (!this.fromRow.data.rel_type || ['one_to_one', 'one_to_many'].includes(this.fromRow.data.rel_type)) {
                canvasHelper.circle(ctx, start.x, start.y, endPointSize)
                ctx.fill()
            } else if (['many_to_many', 'many_to_one'].includes(this.fromRow.data.rel_type)) {
                ctx.moveTo(start.x + arrowLength * directions[from], start.y)
                ctx.lineTo(start.x, start.y + arrowHeight)
                ctx.lineTo(start.x, start.y - arrowHeight)
                ctx.lineTo(start.x + arrowLength * directions[from], start.y)
                ctx.stroke()
            }
            ctx.closePath()

            ctx.beginPath()
            ctx.moveTo(start.x, start.y)
            this.drawPath(ctx, lineType, start, end, controlOne, controlTwo)
            ctx.stroke()
            ctx.closePath()

            ctx.beginPath()
            if (!this.fromRow.data.rel_type || ['one_to_one', 'many_to_one'].includes(this.fromRow.data.rel_type)) {
                ctx.moveTo(end.x, end.y)
                ctx.lineTo(end.x + arrowLength * directions[to], end.y + arrowHeight)
                ctx.lineTo(end.x + arrowLength * directions[to], end.y - arrowHeight)
                ctx.lineTo(end.x, end.y)
                ctx.fill()
            } else if (['one_to_many', 'many_to_many'].includes(this.fromRow.data.rel_type)) {
                ctx.moveTo(end.x + arrowLength * directions[to], end.y)
                ctx.lineTo(end.x, end.y + arrowHeight)
                ctx.lineTo(end.x, end.y - arrowHeight)
                ctx.lineTo(end.x + arrowLength * directions[to], end.y)
                ctx.stroke()
            }
            ctx.closePath()

            this.drawHandles(ctx)
        } finally {
            ctx.restore()
        }
    }

    midpoint() {
        if (!this.pathPoints.length) return { x: 0, y: 0 }
        const middle = (this.pathPoints.length - 1) / 2
        const lower = this.pathPoints[Math.floor(middle)]
        const upper = this.pathPoints[Math.ceil(middle)]
        return {
            x: (lower.x + upper.x) / 2,
            y: (lower.y + upper.y) / 2,
        }
    }

    distanceTo(point) {
        let distance = Number.POSITIVE_INFINITY
        for (let index = 0; index < this.pathPoints.length - 1; index++) {
            distance = Math.min(
                distance,
                distanceToSegment(point, this.pathPoints[index], this.pathPoints[index + 1])
            )
        }
        return distance
    }

    waypointIndexAt(point, tolerance) {
        return this.manualWaypoints.findIndex(waypoint =>
            Math.hypot(point.x - waypoint.x, point.y - waypoint.y) <= tolerance
        )
    }

    waypointInsertionIndex(point) {
        if (!this.manualWaypoints.length) return 0
        return nearestSegmentIndex(point, this.pathPoints)
    }
}
